import { FormikHelpers, useFormikContext } from 'formik';
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useEffect,
} from 'react';

type SubmitHandlerResult = {
  navigationContext?: {
    challengeName?: string;
  };
} | void;

export interface FormPageInfo<T> {
  submitHandler?: ({
    values,
    formikHelpers,
    setIsSubmitting,
    identifyUser,
  }: {
    values: T;
    formikHelpers: FormikHelpers<T>;
    setIsSubmitting: (isSubmitting: boolean) => void;
    identifyUser: (values: T) => void;
  }) => SubmitHandlerResult | Promise<SubmitHandlerResult>;
  validationSchema?: any;
}

type SetFormPageInfoType<T> = Dispatch<SetStateAction<FormPageInfo<T>>>;

const FormPageInfoContext = createContext<
  | {
      setFormPageInfo: SetFormPageInfoType<any>;
    }
  | undefined
>(undefined);

export const FormPageInfoProvider = <T,>({
  formPageInfo,
  setFormPageInfo,
  children,
}: PropsWithChildren<{
  formPageInfo: FormPageInfo<T>;
  setFormPageInfo: SetFormPageInfoType<T>;
}>) => {
  // validate the formik form every time the validation schema changes
  const { validateForm } = useFormikContext();
  useEffect(() => {
    // ensure that validating form happens after formik's internal state for validation finishes updating
    const frameId = requestAnimationFrame(() => {
      validateForm();
    });

    // Cleanup function to cancel the animation frame if component unmounts
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [formPageInfo.validationSchema, validateForm]);

  return (
    <FormPageInfoContext.Provider value={{ setFormPageInfo }}>
      {children}
    </FormPageInfoContext.Provider>
  );
};

export const useSetFormPageInfo = <T,>() => {
  const context = useContext(FormPageInfoContext);
  if (context === undefined) {
    throw new Error(
      'useSetFormPageInfo must be used within a FormPageInfoProvider',
    );
  }
  return context.setFormPageInfo as SetFormPageInfoType<T>;
};
